import { DateTime } from 'luxon'
import { useGeneratedAnnouncementStore } from '@/stores'
import type { AnnouncementJsonldDefaultRead } from '@/api'
import { useConfigStore } from '@/stores/Config.store'
import { computed, ref, type Ref } from 'vue'
import { keyBy } from 'lodash-es'
import { useMercure } from '@/composables/useMercure.ts'
import type { RemovableRef } from '@vueuse/core'

class _AnnouncementService {
  #store?: ReturnType<typeof useGeneratedAnnouncementStore> = undefined
  readonly #data: Ref<Record<string, AnnouncementJsonldDefaultRead>> = ref({})
  #seenAnnouncements?: RemovableRef<number[] | undefined> = undefined
  readonly #setupDone: Ref<boolean> = ref(false)

  async setup() {
    if (this.#setupDone.value) return
    this.#store = useGeneratedAnnouncementStore()
    this.#seenAnnouncements = useConfigStore().get<number[]>('announcements-confirmed', [])
    const announcements = {
      ...(await this.#store.findAllAsIriDictionary({ query: { 'expire[after]': DateTime.now().toISO({ includeOffset: false }) } }, { forceRefresh: true })),
      ...(await this.#store.findAllAsIriDictionary({ query: { 'exists[expire]': false } }, { forceRefresh: true }))
    }

    this.#data.value = keyBy(
      Object.values(announcements).filter((a) => !this.#seenAnnouncements!.value?.includes(a.id!)),
      (a) => a['@id']
    ) as Record<string, AnnouncementJsonldDefaultRead>
    this.#setupDone.value = true

    useMercure().subscribe<AnnouncementJsonldDefaultRead>('announcement', (a) => {
      if (!this.#seenAnnouncements!.value?.includes(a.id!)) {
        this.#data.value[a['@id']!] = a
      }
    })
  }

  isReady() {
    return computed(() => this.#setupDone.value)
  }

  hasUnconfirmed() {
    return computed(() => Object.keys(this.#data.value).length)
  }

  getUnconfirmed() {
    return computed(() => this.#data.value)
  }

  confirmAll() {
    const newConfirmed = this.#seenAnnouncements!.value
    for (const iri in this.#data.value) {
      newConfirmed?.push(this.#data.value[iri].id!)
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this.#data.value[iri]
    }
    this.#seenAnnouncements!.value = newConfirmed
  }
}

export const AnnouncementService = new _AnnouncementService()
